import { useMemo } from "react";
import { useGetMainList } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { InfoTypeResponseDto, DocumentTemplateResponseDto } from "@/gen/api/securefile";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { Typography } from "@mui/material";
import { SimpleOption2 } from "@/components/governance/search/AmbientFacilitiesContext";
import { generalOpOptionExplainations, OperatorOptionExplainationMap, opOptions, OpRuleStates, RuleDisplayTree, 
  PickerOptionAmbientDataContextProps, PickValueOption, RuleDisplayKind, RuleDisplayPresetProps } from "../RuleDisplayPrimitives";


type PickValueKind =
  | 'FileName'
  | 'FilePath'
  | 'FileExtension'
  | 'Computer'
  | 'DocumentType'
  | 'InfoTypes'
  ;
  

const initialRule = (): RuleDisplayTree => ({
  Kind: 'Equals', 
  Left: {Kind: 'PickValue', Value: 'FileName'}, 
  Right: {Kind: 'PickConstant', Value: ''} 
})

const defaultOpLeft = (): RuleDisplayTree['Left'] => ({ Kind: 'PickValue', Value: 'FileName' })
const defaultOpRight = (): RuleDisplayTree['Right'] => ({ Kind: 'PickConstant', Value: '' })

const adjustOpKind = (r:RuleDisplayTree, v:OpRuleStates):RuleDisplayKind|undefined => {
  if (r.Value === 'InfoTypes' && (v.composedRule.Kind !== 'Equals' && v.composedRule.Kind !== 'EqualsAny')) {
    return'EqualsAny'
  }
}

const opOptions_infoType: SimpleOption2[] = [
  opOptions[0], // equals
  opOptions[3], // equalsAny
]

const adjustOpOptions = (r:RuleDisplayTree) => {
  return r.Left?.Value === 'InfoTypes' ? opOptions_infoType : undefined
}


const pvOptions: PickValueOption[] = [
  { key: 'FileName', text: 'File Name', explain: 'A file name should include associated file extension (such as MyFile.docx).' },
  { key: 'FilePath', text: 'File Path', explain: 'A file path should be an absolute path to the file (such as D:\\MyFolder\\MyFile.docx).' },
  { key: 'FileExtension', text: 'File Extension', explain: 'A file extension should include the dot at the front (such as .txt).' },
  { key: 'Computer', text: 'Computer', explain: 'The name of a computer where files are located.' },
  { key: 'DocumentType', text: 'Document Type', explain: 'The Document Type as identified by OnData based on document templates.' },
  { key: 'InfoTypes', text: 'Info Type', explain: 'The Info Type as discovered by OnData in a document.' },
  // { key: 'Classification', text: 'Classification' },
] as any


// -----------------

const opOptionExplainations = {
  Computer: {
    Equals: 'Case insensitive string comparison with a single value in the input.',
    Contains: 'Case insensitive substring comparison with a single value in the input.',
    Matches: 'Case insensitive string match with a single regular expression in the input.',
    EqualsAny: 'Case insensitive string comparison with one or more values in the input (any one match is a hit).',
    MatchesAny: 'Case insensitive string match with one or more regular expressions in the input (any one match is a hit).',
  },
  InfoTypes: {
    Equals: 'The file has an Info Type that matches a single value in the input.',
    EqualsAny: 'The file has an Info Type that matches any one of the values in the input.',
  },
} as OperatorOptionExplainationMap
  
const getOperatorOptionExplainationMap = (r:RuleDisplayTree): OperatorOptionExplainationMap => 
  (opOptionExplainations[r.Left?.Value ?? 'general'] ?? generalOpOptionExplainations['general']) as any

const generateRightExample = (composedRule: RuleDisplayTree) => {
  const pv = composedRule.Left?.Value ?? 'FileName'
  const op = composedRule.Kind
  const bag = (rightExampleMap as any)[pv]
  return bag[op] ?? bag['default']
}

const rightExampleMap = {
  FileName: {
    default: <>Example: <Typography color="info.light" variant="caption">MyFile.docx</Typography></>,
    Matches: 'Enter a valid regular expression.',
    MatchesAny: 'Enter a valid regular expression.',
  },
  FilePath: {
    default: <>Example: <Typography color="info.light" variant="caption">D:\MyFolder\MyFile.docx</Typography></>,
    Matches: 'Enter a valid regular expression.',
    MatchesAny: 'Enter a valid regular expression.',
  },
  FileExtension: {
    default: <>Example: <Typography color="info.light" variant="caption">.txt</Typography></>,
    Matches: 'Enter a valid regular expression.',
    MatchesAny: 'Enter a valid regular expression.',
  },
  Computer: {
    default: '',
    Matches: 'Enter a valid regular expression.',
    MatchesAny: 'Enter a valid regular expression.',
  },
  DocumentType: {
    default: '',
    Matches: 'Enter a valid regular expression.',
    MatchesAny: 'Enter a valid regular expression.',
  },
  InfoTypes: {
    default: '',
  },
} as {[k in 'FileName'|'FilePath'|'FileExtension'|'Computer'|'DocumentType'|'InfoTypes']: Record<string, any>}

// -----------------

const PickerOptionAmbientDataController = (): PickerOptionAmbientDataContextProps => {
  const { data: infoTypesData, ids: ids1 } = useGetMainList<InfoTypeResponseDto&RecPatch>('settings/securefile/info-types', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY'})
  const { data: docuTmplsData, ids: ids2 } = useGetMainList<DocumentTemplateResponseDto&RecPatch>('settings/securefile/document-types', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY'})
  const dataBag = useMemo(() => ({
    infoTypesOptions: (ids1 ?? []).map(id => infoTypesData![id].name!).sort((a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : 1),
    docuTmplsOptions: (ids2 ?? []).map(id => docuTmplsData![id].name!).sort((a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : 1),
  }), [infoTypesData, ids1, docuTmplsData, ids2])
  return dataBag
}

const getPickerOptionProps = (r:RuleDisplayTree) => {
  const useOptions = r.Left?.Kind !== 'PickValue' ? false
    : r.Left?.Value === 'DocumentType' ? 'docuTmplsOptions'
    : r.Left?.Value === 'InfoTypes' ? 'infoTypesOptions'
    : false
  const useOptionsMultiple = !useOptions ? undefined
    : r.Kind === 'Equals' ? false
    : r.Kind === 'EqualsAny' ? true
    : undefined

  return useOptions && useOptionsMultiple != null ? {optionSource: useOptions, multiple: useOptionsMultiple} : undefined
}


export const SecureFilePreset : RuleDisplayPresetProps = {
  defaults: {
    rule: initialRule,
    opLeft: defaultOpLeft,
    opRight: defaultOpRight,
  },
  leftPickeValueOptions: pvOptions,
  pickerAdjust: {
    adjustOpKind,
    adjustOpOptions,
  },
  rightPickerOptions: {
    useDataSource: PickerOptionAmbientDataController,
    getPickerOptionProps,
  },
  explaination: {
    getOperatorOptionExplainationMap,
    generateRightExample,
  }
}

